import { parseDate, today, getLocalTimeZone } from "@internationalized/date";
import _ from "lodash";

import { Member } from "src/patents/patents";
import { missingInformation, requiresGrant } from "../check";
import { IpRight, MaintenanceAction, PcIpRight, ValidationMessage } from "../DennemeyerProvider";
import { DueDateStatus } from "../settings/instruction_timeline";
import { isImported } from "../state/modern_states";
import { IpRightImportStatus, IpRightImportStatusInfo, RenewalNoticeStatusInfo } from "src/renewal/state/modern_states";
import { Instruction, Renewal } from "src/renewal/interfaces/renewal_types";
import { extractDueDateStatus } from "src/renewal/components/dueDates";

// Preparing functions for Notion Flow Chart 
// https://www.notion.so/patent-cockpit/Flow-Charts-for-Renewal-States-566df9d644f34769a7b66f368526600c?pvs=4


export function importStatusExtraction(
    {member, pcIpRight, ipRight, validationErrors}:
    {member: Member, pcIpRight?: PcIpRight, ipRight?: IpRight, validationErrors?: ValidationMessage[]}
): IpRightImportStatusInfo {
    if (member.countryCode === 'WO') {
        return {status: 'not-handled', substatus: 'ignored', reason: 'pct-not-importable'}
    } else if (pcIpRight?.dennemeyerId) {
        return differentiateImportState({member, ipRight, pcIpRight, validationErrors})
    } else if (member.familyMemberStatus === 'in-preparation') {
        return {status: 'not-handled', substatus: 'ignored', reason: 'in-preparation'}
    } else if (member.familyMemberStatus === 'stopped') {
        return {status: 'not-handled', substatus: 'stopped'}
    } else if (requiresGrant(member)) {
        return {status: 'not-handled', substatus: 'ignored', reason: 'not-granted'}
    } else if (member.countryCode === 'EP' && member.familyMemberStatus === 'granted' && member.unitaryPatent !== true) {
        return {status: 'not-handled', substatus: 'ignored', reason: 'granted-ep'}
    } else {
        const missing = missingInformation(member)
        // console.log({missing})
        if (missing.length > 0) {
            return { status: 'not-handled', substatus: 'missing-data', messages: missing }
        } else {
            return { status: 'not-handled', substatus: 'can-import' }
        }
    }
}

function hasRecentChanges(pcIpRight: PcIpRight) {
    const now = today(getLocalTimeZone())
    const updated = parseDate(pcIpRight.updated.slice(0, 10))
    return (now.subtract({ days: 7 }) < updated && !pcIpRight.dirty)
}

function differentiateImportState({ipRight, pcIpRight, validationErrors}: {member: Member, ipRight?: IpRight, pcIpRight: PcIpRight, validationErrors?: ValidationMessage[]}): IpRightImportStatusInfo {
    //console.log({ipRight, pcIpRight, validationErrors})
    // Ignore errors if it's actually ready at DM
    /*if (ipRight?.Status?.includes('Granted') && ipRight?.IntegrationStatus === 'Imported') {
        return {status: 'ready', substatus: 'fully-ready'}
    } else*/ if (validationErrors?.length > 0) {
        return {status: 'not-handled', substatus: 'has-errors', messages: validationErrors.map(e => e.message)}
    } else if (ipRight?.Status?.includes('Inactive')) {
        if (pcIpRight.updated !== undefined && (pcIpRight.status === 'Granted' || pcIpRight.status === 'Pending')) {
            if (hasRecentChanges(pcIpRight)) {
                // The IP Right was stopped and now revived again but the back office didn't see it yet
                return { status: 'ready', substatus: 'back-office-processing' }
            }
        } // default case:
        return { status: 'not-handled', substatus: 'stopped' }
    } else if (ipRight === undefined) {
        return {status: 'ready', substatus: 'back-office-processing'}
    } else if (ipRight?.NextMaintenanceActionDueDate !== undefined && ipRight?.NextMaintenanceActionDueDate === ipRight?.ExpiryDate) {
        return {status: 'ready', substatus: 'paid-for-life'}
    } else {
        if (pcIpRight.status === 'Inactive' && hasRecentChanges(pcIpRight)) {
            return { status: 'not-handled', substatus: 'stopped' }
        }
        // default case
        return {status: 'ready', substatus: 'fully-ready'}
    }
}

// export function isMaintenanceActionPhaseOpen(phase: MaintenanceActionPhase) {
//     return phase === "Auto-Pay in Progress" || phase === "Waiting For Instruction"
// }

// Make sure to run `eliminateDoubleMaintenanceActions` first on the maintenanceActions before passing them in
export function renewalNoticesStatus({
    renewalStatus, maintenanceActions, renewals,
}: {
    renewalStatus: IpRightImportStatus, 
    maintenanceActions?: MaintenanceAction[],
    renewals?: Renewal[],
}): RenewalNoticeStatusInfo & {total: number} {
    if ((renewals ?? []).length === 0) {
        const status = renewalStatus === 'ready' ? 'ready' : 'not-ready'
        return {status, total: 0}
    } else {
        const lastRenewal = _.maxBy(renewals, r => r.instructionDueDate)
        // const lastMaintenanceAction = _.maxBy(maintenanceActions, ma => ma.DueDate)
        // const dueDate = calculateDueDates(lastMaintenanceAction.DueDate)
        const maintenanceAction = maintenanceActions.find(ma => ma.DennemeyerId === lastRenewal.id)
        const today = new Date().toISOString().slice(0, 10)
        const status = extractDueDateStatus(lastRenewal, today)
        return {
            ...maintenanceActionStatus({ maintenanceAction, ...lastRenewal, status}), 
            total: maintenanceActions.length,
            id: maintenanceAction.DennemeyerId,
        }
    }
}

function maintenanceActionStatus(
    { maintenanceAction, instruction, instructionDueDate, status }: 
    { maintenanceAction: MaintenanceAction, instructionDueDate: string, status: DueDateStatus, instruction?: Instruction }
): RenewalNoticeStatusInfo {

    // if there is no instruction, let's not consider it as instructed although DM says so
    const isInstructed = (maintenanceAction.Phase === "Instructed / In Progress" && (instruction !== undefined || maintenanceAction.InstructionInfo !== undefined)) 
        || maintenanceAction.Phase === "Closed" || instruction !== undefined

    if (isInstructed) {
        if (maintenanceAction.InstructionInfo?.Instruction === 'Pay' || instruction?.instruction === 'Pay') {
            return {status: 'instructed', instruction: 'Pay', date: (maintenanceAction.InstructionInfo?.InstructedAt ?? instruction?.created)?.slice(0, 10)}
        } else if (maintenanceAction.InstructionInfo?.Instruction === 'Cancel' || instruction?.instruction === 'Cancel') {
            return {status: 'instructed', instruction: 'Cancel', date: (maintenanceAction.InstructionInfo?.InstructedAt ?? instruction?.created)?.slice(0, 10)}
        } else {
            return {status: 'instructed', date: (maintenanceAction.InstructionInfo?.InstructedAt ?? instruction?.created)?.slice(0, 10), autoPay: maintenanceAction.PermanentOrder}
        }
    } else {
        if (maintenanceAction.PermanentOrder) { // auto-pay
            if (maintenanceAction.Phase.includes('Inactive')) {
                return { status: 'fee-will-not-be-paid' }
            }
            const today = new Date().toISOString().slice(0, 10)
            const isPast = instructionDueDate <= today
            return { status: isPast ? 'payment-has-been-triggered' : 'payment-will-be-triggered', date: instructionDueDate }
        } else {
            if (status === 'open' || status === 'early' || status === 'critical') {
                return { status: 'fee-will-not-be-paid-unless-instructed' }
            } else if (status === 'manual-instruction') {
                return { status: 'fee-will-not-be-paid' }
            } else {
                return { status: 'fee-has-not-been-paid' }
            }
        }
    }
}


export function canBeImported(
    {oldMember, newMember, pcIpRight, ipRight}:
    {oldMember?: Member, newMember: Member, pcIpRight: PcIpRight, ipRight: IpRight, validationErrors?: ValidationMessage[]}) {
    const newStatus = importStatusExtraction({member: newMember, pcIpRight, ipRight})
    //console.log({newStatus})
    if (newStatus.substatus === 'can-import') {
        if(oldMember === undefined) {
            return true
        }
        const oldStatus = importStatusExtraction({member: oldMember, pcIpRight, ipRight})
        //console.log({oldStatus})
        //console.log({oldStatus, result: !(oldStatus.status === 'can-import' || isImported(oldStatus.status, ipRight)), imported: isImported(oldStatus.status, ipRight)})
        return !(oldStatus.substatus === 'can-import' || isImported(oldStatus))
    } else {
        return false
    }
}

export function canBeStopped(
    {oldMember, newMember, pcIpRight, ipRight, validationErrors}: 
    { oldMember?: Member, newMember: Member, pcIpRight?: PcIpRight, ipRight?: IpRight, validationErrors?: ValidationMessage[] }) {

    if (oldMember?.familyMemberId === undefined) return false

    const oldStatus = importStatusExtraction({ member: oldMember, pcIpRight, ipRight, validationErrors })
    return oldStatus.status === 'ready' && newMember.familyMemberStatus === 'stopped' && oldMember.familyMemberStatus !== 'stopped'
}